import type {
  PointerEvent as ReactPointerEvent,
  TouchEvent as ReactTouchEvent,
} from 'react';
import { useRef } from 'react';

import { useOverlay } from '../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { useTooltip } from '../../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { useTooltipActions } from '../../core/components/overlay-tooltip/useTooltipActions.js';
import { isTapGesture } from '../../core/components/overlay-tooltip/utils/tap-gesture.js';
import { useSetOverlayState } from './store/use-set-overlay-state.js';

export const useOverlayHandlers = () => {
  const { setOverlayStateInBounds } = useSetOverlayState();
  const overlay = useOverlay();
  const tooltipStore = useTooltip();
  const { unpin } = useTooltipActions();
  const touchStartPosRef = useRef<{ x: number; y: number } | null>(null);

  const onPointerEnterHandler = () => {
    setOverlayStateInBounds(true);
  };

  const onPointerLeaveHandler = (event: ReactPointerEvent) => {
    if (event.pointerType !== 'touch') {
      setOverlayStateInBounds(false);
    }
  };

  const onEmptyAreaTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!overlay.overlayRef.current?.contains(event.target as Node)) {
      touchStartPosRef.current = null;
      return;
    }
    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }
    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const onEmptyAreaTouchEnd = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!overlay.overlayRef.current?.contains(event.target as Node)) {
      return;
    }
    if (
      event.target instanceof Element &&
      event.target.closest('button, a, [role="button"]') !== null
    ) {
      return;
    }
    const touch = event.changedTouches[0];
    if (!touch) {
      return;
    }
    const start = touchStartPosRef.current;
    touchStartPosRef.current = null;
    if (start !== null && !isTapGesture(start, touch)) {
      return;
    }
    if (event.cancelable) {
      event.preventDefault();
    }
    if (tooltipStore.getState().pinned) {
      unpin();
    }
  };

  return {
    onPointerEnterHandler,
    onPointerLeaveHandler,
    onEmptyAreaTouchStart,
    onEmptyAreaTouchEnd,
  };
};
